// Single source of view-renderer styling. "Style all widgets to Monad afterwards" =
// edit this object (+ the CSS variables the class names resolve to). Renderers MUST
// read from here — no hardcoded dims / colors / mark geometry per renderer.
/** SEN-165: the map style of the offline e2e build — a plain background, no
 *  sources, so MapLibre makes no network request for it. */
export const E2E_OFFLINE_MAP_STYLE = {
  version: 8 as const,
  sources: {},
  layers: [{ id: 'background', type: 'background' as const, paint: { 'background-color': '#eef1f4' } }],
}

import { CARD_LOOK, CHART_LOOK, SUMMARY_ROW_GRID_CLASS } from './themeLook'

export const viewTheme = {
  /** Record-identity colours are persisted values, so this picker palette uses
   *  codec-valid hex tokens rather than CSS-variable chart paints. */
  identity: {
    palette: [
      '#2563eb', '#7c3aed', '#db2777', '#dc2626', '#ea580c',
      '#ca8a04', '#16a34a', '#0891b2', '#0284c7', '#4f46e5',
    ],
  },
  /** SVG canvas coordinate space. svgClass applies to every frame-renderer SVG element
   *  to enforce consistent overflow containment (max-height + full width). */
  // max-w cap removed in F1 (SEN-1398): charts are container-measured now
  // (useMeasuredCanvas) — the CONTAINER decides width, the class only guards overflow.
  // F1: cartesian SVGs get EXPLICIT measured width/height attrs (no aspect
  // scaling — an h-auto SVG whose height derives from the measured box feeds
  // back into the ResizeObserver and inflates forever). 'block' kills the
  // inline-SVG baseline gap; sizing is the width/height attributes.
  canvas: { width: 480, height: 240, pad: 24, svgClass: 'block max-w-full',
    // Cartesian H derives from measured W ONLY (aspect-clamped) — deriving H
    // from the measured wrapper height loops in auto-height hosts (cold read
    // H1: gallery cards grew unboundedly; App only converged because its host
    // happens to have a definite height).
    aspect: 0.45, maxHeight: 420,
    /** Block density (a dashboard tile) has a DEFINITE height from the grid,
     *  so height is safe to consume there: the tile is a flex column, the
     *  caption / legend / summary strip take their natural height and the
     *  svg is measured from the slot that remains (never a fixed reserve —
     *  it clipped the strip). A taller tile = a taller chart, with no
     *  reload (CEO 2026-09-02). */
    blockMinHeight: 120,
    /** Tall scrollable chart WRAPPER (gantt/tree) — the wrapper owns scroll. */
    scrollWrapClass: 'relative max-h-[70vh] overflow-y-auto',
    /** Tall scrollable charts (gantt, tree): the WRAPPER owns max-h + scroll;
     *  a max-h on the SVG itself would scale the chart down instead (F1). */
    svgClassTall: 'h-auto w-full' },
  bar: { ...CHART_LOOK.bar, maxCategories: 24 },
  line: CHART_LOOK.line,
  /** SEN-1730 (design review 2026-09-02, F9): the series TERMINATOR. A series
   *  that stops before the others do ends mid-plot, and a line that just stops
   *  reads as a zero. It ends in a HOLLOW point instead — the page ground
   *  inside, the line's own colour on the rim — the frontier's "series
   *  terminates here" glyph (Plot/Highcharts), never a filled dot (which reads
   *  as one more data point). `fill` is the ground token, so the point is
   *  hollow in both themes.
   *
   *  NB "terminator", not "mark": `mark` is taken by the FrameMark vocabulary
   *  (contract.ts, marksConformance.test.ts). This glyph is a DECORATION of
   *  the line mark, not a mark kind of its own. */
  seriesTerminator: { radius: 4, strokeWidth: 2, fill: 'hsl(var(--background))', strokeClass: 'stroke-primary' },
  /** Zero baseline drawn on cartesian charts whose y-domain crosses zero (SEN-1326 sign fix). */
  baseline: CHART_LOOK.baseline,
  /** hitRadius floors LineMark's invisible hover target; the DRAWN radius is
   *  per-band (`viewTheme.scale.steps.*.pointRadius`). */
  point: { hitRadius: 4, fillClass: 'fill-primary' },
  /** Line crosshair (hover audit 2026-09-02): dashed guide at the nearest x,
   *  one dot per series in that series' colour, values stacked in the tooltip. */
  crosshair: { strokeClass: 'stroke-muted-foreground', dash: '3 3', dotRadius: 3.5 },
  /** Owner 2026-10-30, drag-to-zoom: the band a drag across a time axis
   *  selects — the primary token, faint, so the lines stay readable under it. */
  brush: { fillClass: 'fill-primary/10', strokeClass: 'stroke-primary/60', strokeWidth: 1, minBuckets: 2 },
  /** offsetPx: vertical gap between the x-axis baseline and axis tick labels (below).
   *  maxChars: maximum characters rendered per axis label before truncation. */
  axisLabel: { ...CHART_LOOK.axisLabel, offsetPx: 12, maxChars: 10 },
  /** Numeric/time axes (F1, SEN-1398). gridClass strokes horizontal gridlines.
   *  SEN-1432 S1: yTickCount/xTickCount/yGutter deleted — tick counts are now
   *  pixel budgets (viewTheme.scale.budgets) resolved by scale.ts, and the left
   *  gutter is measured from real tick text (M4). `regular`'s floor (44) is the
   *  C3 regression floor. */
  axis: CHART_LOOK.axis,
  /** Multi-series palette — the FALLBACK. A series bound to a record that
   *  carries its own `color` attribute (energy sources, SEU categories) is
   *  drawn in that colour first (see views/seriesColor.ts); the palette is
   *  for identities with no colour of their own (building types, countries).
   *  Index-cycled; six hues so six sources do not wrap. */
  series: CHART_LOOK.series,
  /** Sequential ramp for intensity (heatmap cells, weekday bars): one hue,
   *  eight lightness stops, low → high. The renderer picks the stop from a
   *  domain it computes PER comparison group (heatmap `scale_by`) — a
   *  distribution centre must not wash every store into ramp-1. */
  ramp: {
    stops: [
      'hsl(var(--ramp-1))', 'hsl(var(--ramp-2))', 'hsl(var(--ramp-3))', 'hsl(var(--ramp-4))',
      'hsl(var(--ramp-5))', 'hsl(var(--ramp-6))', 'hsl(var(--ramp-7))', 'hsl(var(--ramp-8))',
    ],
    /** Cell text flips to the light foreground from this stop (0-based). */
    invertTextFrom: 5,
    cellGapPx: 3,
    cellRadiusClass: 'rounded-[3px]',
    /** a heatmap cell: square-ish at a pane's width, never collapsing */
    cellClass: 'block h-[18px] min-w-[6px]',
    legendSwatchClass: 'h-3 w-5',
    /** the legend's one gradient bar between min and max */
    legendBarClass: 'h-2 w-24 shrink-0 rounded-full',
    /** an outlined cell's ring, by the time part it falls in (operating zone); a part
     *  not listed (closed) draws no ring */
    // ramp-up / ramp-down only: the open hours read from the colour itself (owner, 2026-10-23)
    outline: {
      ramp_up: 'hsl(var(--zone-ramp-up))',
      ramp_down: 'hsl(var(--zone-ramp-down))',
    } as Record<string, string>,
  },
  /** Semantic state — good / warn / critical. Separate from the accent and
   *  never used as one; the stat tile's delta badge and threshold marks only. */
  semantic: {
    good: { badgeClass: 'bg-success/10 text-success', dotClass: 'bg-success' },
    warn: { badgeClass: 'bg-warning/10 text-warning', dotClass: 'bg-warning' },
    critical: { badgeClass: 'bg-destructive/10 text-destructive', dotClass: 'bg-destructive' },
    /** SEN fix2 (R9): "no comparison" / "flat" share the stat badge's OWN
     *  geometry (rounded-md, same size as good/warn) — not a borrowed
     *  map icon-stack chip, which visibly changed shape with tone. */
    neutral: { badgeClass: 'bg-muted text-muted-foreground', dotClass: 'bg-muted-foreground' },
  },
  /** Supporting-figure strip under pattern charts (peak / quietest / average). */
  summary: {
    /** THE summary grid rule (SEN-433, A20): as many equal columns of at
     *  least 11rem as fit, collapsing empty tracks — a collection summary's
     *  row (SummaryStrip, `gap-2`) and a dashboard's row of self-framed cards
     *  (the Portfolio KPI row, `gap-4`). ONE copy of the column rule; each
     *  surface keeps its own gap. */
    rowGridClass: SUMMARY_ROW_GRID_CLASS,
    /** The same rule for a LONE figure (a list's count, SEN-437): auto-fill keeps
     *  the empty tracks, so one card keeps a card's width instead of stretching
     *  across the page. Same 11rem minimum; only the track keyword differs. */
    loneGridClass: 'grid min-w-0 [grid-template-columns:repeat(auto-fill,minmax(11rem,1fr))]',
    stripClass: 'mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3',
    /** A collection's summary (count + a total per metric column) above its
     *  rows: one line of tiles that wraps, as many as the view has metrics. */
    rowClass: 'flex flex-wrap gap-2',
    rowItemClass: 'min-w-[9rem] max-w-[16rem] flex-1 rounded-md bg-muted/40 px-3 py-2',
    itemClass: 'rounded-md bg-muted/40 px-3 py-2',
    labelClass: 'text-[10px] font-medium uppercase tracking-wide text-muted-foreground',
    valueClass: 'tabular mt-0.5 text-base font-semibold text-foreground',
    noteClass: 'text-xs text-muted-foreground',
  },
  /** stat tile typography — one number, its unit, its name, its period. */
  stat: {
    valueClass: 'tabular font-semibold leading-none tracking-tight text-foreground',
    /** Figure size scales with the tile between these (px). */
    valueMinPx: 22,
    valueMaxPx: 72,
    /** Below this measured height the figure centres; taller cards anchor it
     *  to the bottom so label + period read as a caption. Geometry (Task 10
     *  fix round 1, R3/F11 — task-10-review.md Important #3): the h=2 band
     *  was measured at 94–158px in an earlier cold read, but the page's
     *  FITTED cell (useFittedCell, viewTheme.dashboard.cellPx=104) commonly
     *  hits its 104px ceiling at real widths — an h=2 tile then measures
     *  2*104 − tileGapPx(12) = 196px, ABOVE the old 160px threshold, so
     *  every KPI tile anchored to the bottom instead of centring and left
     *  ~122px of dead white above the figure (measured on all three Monthly
     *  Store Report screenshots). Raised to 200 — covers the full h=2 range
     *  up to the fitted-cell ceiling while staying well under h=3's 300px,
     *  so nothing taller starts centring by accident. See StatRenderer.tsx
     *  for the companion fix: the anchor now applies to an inner flex-1
     *  wrapper, not the outer card — an `mt-auto` annotation region as the
     *  LAST flex child was absorbing 100% of the free space itself (a CSS
     *  auto-margin always wins over a sibling's justify-content), so the
     *  anchor toggle was previously inert whenever that div rendered, even
     *  empty. */
    centreBelowPx: 200,
    unitClass: 'text-[0.4em] font-normal text-muted-foreground',
    labelClass: 'mt-2 text-sm text-foreground',
    periodClass: 'text-xs text-muted-foreground',
    iconBoxClass: 'inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground',
    badgeClass: 'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium tabular',
    /** R8 (Task 9 fix round 1): the annotation region (FrameAnnotations —
     *  warnings/dropped/captions) must never push the figure out of the
     *  card. A 7-line ratio-denominator refusal in a w=3 h=2 card (94-158px)
     *  overflowed BOTH directions — `justify-center` split the overflow, so
     *  the figure clipped at the top and the warning text spilled past the
     *  card border (identical on every "Intensity vs last year" screenshot).
     *  Clamped to a fixed max-height (~3 lines of text-xs) + hidden overflow;
     *  the FULL text still reaches the reader via the wrapper's `title`
     *  (see `stat-annotations` in StatRenderer.tsx). Bounded, not zero — a
     *  short warning still reads in full without clamping. */
    /** I3 (fix wave): `max-h-12 overflow-hidden` alone clipped the text with
     *  no visual cue anything was cut — a 7-line refusal read as if it were
     *  the WHOLE message, truncated mid-sentence ("The comparison window
     *  [2025-08-"), the clip a reader cannot tell from a full sentence.
     *  `line-clamp-3` implies `text-overflow: ellipsis` (Tailwind ≥3.3, no
     *  plugin needed) so the clamp reads as a clamp; `max-h-12
     *  overflow-hidden` stays as the pixel-height backstop (R8) — the full
     *  text still reaches the reader via the wrapper's `title`. */
    annotationClampClass: 'line-clamp-3 max-h-12 overflow-hidden',
  },
  tooltip: {
    // 'fixed': positioned from the pointer's clientX/Y — never distorts SVG space
    className:
      'pointer-events-none fixed z-40 rounded border border-border bg-background px-2 py-1 text-xs shadow-float',
    offsetPx: 10,
  },
  legendUi: {
    containerClass: 'mt-1 flex flex-wrap items-center gap-3 px-1',
    itemClass: 'inline-flex cursor-pointer items-center gap-1 text-xs text-muted-foreground select-none',
    swatchSize: 8,
    hiddenClass: 'opacity-40 line-through',
  },
  /** SEN-1432 (S0): the autoscale seam — continuous pixel budgets + discrete
   *  size bands + per-band resolution steps. Consumed ONLY by
   *  views/scale.ts resolveViewScale (§3a/§3b); NOT read by any renderer yet
   *  (S1 wires the axis kit). REGRESSION FLOOR: the `regular` step reproduces
   *  today's constants byte-for-byte, so every later slice diffs against a
   *  known-good baseline and reverting S0 restores exactly today's geometry. */
  scale: {
    /** Continuous budgets — px of axis length per tick / label / mark (M1/M2).
     *  Frontier anchors: Plot xTick 80 / yTick 35, uPlot ≥50 / ≥30 — we sit
     *  slightly below because our type is 10px against their 12px. Exact
     *  values are pinned by S1 tests, not decided here. */
    budgets: {
      xTickPx: 64,      // px per x tick (at the legacy canvas: floor(412/64) = today's 6)
      yTickPx: 38,      // px per y tick (at the legacy canvas: floor(192/38) = today's 5)
      bandLabelPx: 64,  // px per category label — today's hardcoded 64 in Axis.tsx:37
      minBarPx: 10,     // C18 admissible [9,13]: <9 never binds above the 294 floor; >=14 drops 480 below maxCategories
      valueLabelPx: 28, // px of mark width needed for a value label — today's valueLabel.minBarWidth
      legendItemPx: 72, // px per legend item before collapse (S3 tunes)
    },
    /** Discrete size bands — thresholds only; the band CHOICE lives in
     *  resolveViewScale (W-driven, measured — never wire metadata, §3c). */
    bands: {
      micro: { maxW: 0 },     // reachable only below the kind's floor (§3d) → renderer refuses, never a clipped chart
      compact: { maxW: 479 }, // dashboard tiles, chat, peek rail (min tile 294 sits comfortably inside)
      regular: { maxW: 899 }, // 480 = today's canvas.width — regular starts where the old fixed chart was
      wide: { maxW: Infinity },
    },
    /** Per-band discrete resolutions. `regular` is the regression floor.
     *  SEN-1432 S1 (C13): `compact.gutterMin` lowered to 20 so the M4 measured
     *  gutter is load-bearing at narrow tiles (294px) instead of inert under a
     *  40px floor; `wide.gutterMin` back at 44 so W=1200 keeps today's 44px
     *  gutter (the S1 plan expects "1200 — more ticks, gutter still 44"). */
    steps: {
      // Design review 2026-09-02 (F7): the type floor. Ticks and value labels
      // rendered at 8–9px across a dashboard — below what a reader resolves
      // at arm's length. 10px is the floor at the narrowest band, 11px the
      // working size, 12px on wide tiles.
      micro: { tickFont: 10, valueFont: 9, gutterMin: 40, barGapPx: 1, pointRadius: 3, legend: 'off', valueLabels: 'off' },
      compact: { tickFont: 11, valueFont: 10, gutterMin: 20, barGapPx: 1, pointRadius: 3, legend: 'collapsed', valueLabels: 'off' },
      regular: { tickFont: 11, valueFont: 10, gutterMin: 44, barGapPx: 2, pointRadius: 4, legend: 'inline', valueLabels: 'on' },
      wide: { tickFont: 12, valueFont: 11, gutterMin: 44, barGapPx: 3, pointRadius: 5, legend: 'inline', valueLabels: 'on' },
    },
  },
  warning: { className: 'px-2 py-1 text-xs text-amber-600' },
  /** A table's calculated-column warnings: bounded (~5 lines) and scrollable, so a long list (one
   *  note per uncovered column) never pushes the rows out of a dashboard tile or down the page. The
   *  full text stays readable by scrolling, never clipped. */
  warningList: { className: 'px-2 py-1 text-xs text-amber-600 max-h-20 shrink-0 overflow-y-auto' },
  noteToggle: { className: 'px-2 py-0.5 text-xs text-muted-foreground shrink-0' },
  /** SEN-1411: threshold marks — agent-supplied reference lines. */
  markThreshold: { strokeClass: 'stroke-amber-600', strokeWidth: 1, dash: '4 3' },
  /** SEN-1411: caption marks — agent-supplied explanations, info tone (distinct from warnings). */
  caption: { className: 'px-2 py-1 text-xs text-muted-foreground italic' },
  /** an info-severity note inside the warnings list: caption tone, not amber */
  noteTone: { className: 'text-muted-foreground italic' },
  /** SEN-1730: every annotation banner is a LIST — captions, warnings + the
   *  renderer's own notes, dropped entries. Bare sibling spans ran together
   *  into one sentence ("No readings after 2026-08-31District Heating: last
   *  reading 2026-07-31" — the live verify screenshot); one fact per line. */
  noteList: { className: 'flex flex-col gap-0.5' },
  /** T6/F2a: month-grid calendar. SEN-1422: leading ISO-week gutter column
   *  (first grid track) + day-overflow popover tokens. */
  calendar: {
    containerClass: 'flex flex-col gap-1 p-2',
    monthRowClass: 'flex items-center justify-between px-1',
    monthLabelClass: 'text-sm font-semibold text-foreground',
    /** a quiet note beside the month (what the grid could not place) */
    noteClass: 'cursor-help text-[11px] text-muted-foreground',
    navClass: 'flex items-center gap-1',
    navBtnClass: 'rounded-sm px-2 py-0.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
    gridClass: 'grid grid-cols-[2rem_repeat(7,minmax(0,1fr))] gap-px rounded-sm border border-border bg-border',
    weekdayClass: 'bg-background px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground',
    /** SEN-1422: ISO week number gutter cell (leading column). */
    weekNumClass: 'bg-background px-1 py-1 text-right text-[10px] tabular-nums text-muted-foreground',
    dayClass: 'min-h-[72px] bg-background p-1 align-top',
    /** days outside the month: quiet, the same ground as a day (no filled block) */
    padClass: 'min-h-[72px] bg-background',
    dayNumClass: 'text-[10px] text-muted-foreground',
    /** an event's title wraps to two lines before it is cut */
    eventClass: 'mt-0.5 block w-full line-clamp-2 break-words rounded-sm bg-secondary px-1 py-0.5 text-left text-[11px] leading-snug text-foreground disabled:cursor-default',
    /** the day a dragged event would land on */
    dayDropClass: 'bg-primary/10 ring-1 ring-inset ring-primary/50',
    overflowClass: 'mt-0.5 px-1 text-[10px] text-muted-foreground',
    /** SEN-1422: the +N chip when it IS a control (interactions present). */
    overflowBtnClass: 'mt-0.5 w-full rounded-sm px-1 text-left text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground',
    /** SEN-1422: day "more" popover (radix, house pattern — ColumnHeaderMenu). */
    popoverClass: 'z-50 min-w-[11rem] max-w-[16rem] rounded-float border border-border bg-background py-1 shadow-float',
    popoverTitleClass: 'px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground',
    popoverItemClass: 'block w-full truncate px-3 py-1.5 text-left text-xs text-foreground hover:bg-muted disabled:cursor-default',
    /** SEN-1423 (calendar spans): a multi-day bar lane laid over each week. The
     *  lane is a nested grid using the SAME column template as `gridClass`
     *  (2rem gutter + 7 equal day columns) so bars align with the day cells
     *  below; bars span columns via inline gridColumn. */
    spanLaneClass: 'col-span-full grid grid-cols-[2rem_repeat(7,minmax(0,1fr))] gap-px',
    spanBarClass: 'flex min-h-[18px] items-center overflow-hidden rounded-sm bg-primary px-1 text-[11px] text-primary-foreground disabled:cursor-default',
    /** SEN-1424: the scale switcher in the kind's toolbar chrome (month | week |
     *  year — rendered from config_schema, never a hardcoded list). */
    scaleClass: 'flex items-center gap-0.5',
    scaleBtnClass: 'rounded-sm px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground',
    /** the chosen scale: the quiet selected tone of a segmented control, not a primary block */
    scaleBtnActiveClass: 'rounded-sm bg-muted px-2 py-0.5 text-xs font-medium text-foreground',
  },
  /** SEN-1424: week scale — seven Monday-first day columns WITH intra-day time
   *  positioning (metadata-driven: timestamp → positioned block, date → all-day
   *  strip). hourPx is the ONE vertical unit every position derives from. */
  calendarWeek: {
    containerClass: 'flex flex-col gap-1 p-2',
    headerRowClass: 'flex items-center justify-between gap-2 px-1',
    weekLabelClass: 'text-sm font-semibold text-foreground',
    weekNumClass: 'rounded-sm bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground',
    navClass: 'flex items-center gap-1',
    navBtnClass: 'rounded-sm px-2 py-0.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
    gridClass: 'grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] gap-px rounded-sm border border-border bg-border',
    gutterClass: 'bg-background',
    hourLabelClass: 'relative h-10 text-right text-[10px] tabular-nums text-muted-foreground',
    hourLabelInnerClass: 'absolute right-1 top-0 -translate-y-1/2',
    dayHeadClass: 'bg-background px-1 py-0.5 text-center text-[10px] font-medium uppercase text-muted-foreground',
    dayHeadTodayClass: 'bg-background px-1 py-0.5 text-center text-[10px] font-semibold uppercase text-primary',
    allDayStripClass: 'grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] gap-px rounded-sm border border-border bg-border',
    allDayLabelClass: 'bg-background px-1 py-0.5 text-right text-[10px] uppercase text-muted-foreground',
    allDayCellClass: 'bg-background p-0.5 align-top',
    allDayEventClass: 'mt-0.5 block w-full truncate rounded-sm bg-secondary px-1 py-0.5 text-left text-[11px] text-foreground disabled:cursor-default',
    dayColumnClass: 'relative bg-background',
    hourLineClass: 'absolute left-0 right-0 border-t border-border/50',
    timedEventClass: 'absolute left-0 right-0 overflow-hidden rounded-sm bg-primary px-1 text-[11px] leading-tight text-primary-foreground disabled:cursor-default',
    /** current-time line across today's column — the gantt today-line convention
     *  (markThreshold token, amber). */
    nowLineClass: 'absolute left-0 right-0 border-t border-amber-600',
    /** height of one hour in px — the ONE vertical unit for day-column height. */
    hourPx: 40,
  },
  /** SEN-1422: month-mini compact variant (calendar at block density). */
  monthMini: {
    containerClass: 'flex flex-col gap-1 p-2',
    monthLabelClass: 'px-1 text-xs font-semibold text-foreground',
    gridClass: 'grid grid-cols-7 gap-px',
    weekdayClass: 'px-0.5 text-center text-[9px] font-medium uppercase text-muted-foreground',
    dayClass: 'flex min-h-[24px] flex-col items-center rounded-sm bg-background p-0.5',
    padClass: 'min-h-[24px]',
    dayNumClass: 'text-[9px] tabular-nums text-muted-foreground',
    dotsRowClass: 'flex items-center gap-0.5',
    dotClass: 'inline-block h-1 w-1 rounded-full bg-primary',
    /** dots shown per day, capped — the cell title carries the true count */
    dotCap: 3,
  },
  /** T6/F2a: agenda compact variant (calendar at peek/block). */
  agenda: {
    containerClass: 'flex flex-col gap-1',
    listClass: 'flex flex-col',
    itemClass: 'flex w-full items-baseline gap-2 rounded-sm px-2 py-1 text-left text-xs hover:bg-muted disabled:cursor-default',
    dateClass: 'shrink-0 tabular-nums text-muted-foreground',
    labelClass: 'min-w-0 truncate text-foreground',
  },
  /** SEN-1411: column-counts compact variant (kanban at peek/block). */
  columnCounts: {
    containerClass: 'flex flex-col gap-1',
    rowClass: 'flex flex-wrap items-center gap-1.5 px-2 py-1',
    chipClass: 'rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs text-foreground disabled:cursor-default',
  },
  empty: { className: 'p-4 text-sm text-muted-foreground' },
  /** labelGutter: left column reserved for row labels so bars never overdraw them. */
  /** minBarPx: single-day/zero-length intervals render a visible tick, not a 1px sliver (design review). */
  interval: { barHeight: 14, rowGap: 8, fillClass: 'fill-primary', labelGutter: 120, labelMaxChars: 16, minBarPx: 3 },
  /** columnFlex: CSS flex shorthand for kanban columns — prevents columns from shrinking. */
  board: {
    /** Lanes share the board's width equally (flex 1 1 0) between a minimum —
     *  below it the board scrolls sideways — and a maximum, so a card's text
     *  never runs too wide on a huge screen. Cards fill their lane. */
    columnMinWidth: 280,
    columnMaxWidth: 480,
    columnGap: 12,
    columnFlex: '1 1 0',
    /** T6/F2b: WIP count chip beside the lane title. */
    countClass: 'ml-1.5 rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground',
    columnClass: 'rounded-md border border-border bg-muted/30 p-2',
    /** the lane a dragged card would land in */
    dropClass: 'border-primary/60 bg-primary/5',
    cardClass: 'group/card flex flex-col gap-1 rounded-md border border-border bg-background px-2.5 py-2 text-xs outline-none hover:border-foreground/20 focus-visible:ring-1 focus-visible:ring-ring',
    cardHeadClass: 'flex min-w-0 items-center gap-1 truncate text-[11px] tabular-nums text-muted-foreground',
    cardTitleClass: 'line-clamp-3 text-[13px] leading-snug text-foreground',
    cardFootClass: 'mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground',
    cardMenuClass: 'rounded px-1 leading-none text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/card:opacity-100',
    menuClass: 'z-50 min-w-[11rem] rounded-float border border-border bg-background py-1 shadow-float',
    menuTitleClass: 'px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground',
    menuItemClass: 'flex w-full items-center px-3 py-1.5 text-left text-xs text-foreground outline-none hover:bg-muted focus-visible:bg-muted focus-visible:ring-0 focus-visible:ring-offset-0',
    headerClass: 'mb-2 flex items-center px-0.5 text-xs font-medium text-muted-foreground',
  },
  tree: {
    nodeRadius: 6,
    levelGap: 64,
    siblingGap: 80,
    nodeFillClass: 'fill-primary',
    edgeStrokeClass: 'stroke-muted-foreground',
    edgeStrokeWidth: 1,
    labelClass: 'fill-foreground',
    labelFontSize: 10,
  },
  // Map (layout_kind="geo"). Marker geometry + palette + basemap are ALL tokens
  // here so "restyle to Monad" stays one edit (SEN-604). Colors are CSS-variable
  // references (resolve against index.css :root / .dark) — never hardcoded hex.
  // minRadius/maxRadius bound the area-proportional measure scale (radius ∝ √value);
  // pointRadius is the uniform fallback when no measure role is bound.
  map: {
    pointRadius: 5,
    // Area-proportional radius range. 22px max opaque circles occluded each
    // other on a 122-site country (SEN-1721 verification); the map reads as
    // a field of dots whose AREA carries the value, not as bubbles.
    minRadius: 3,
    maxRadius: 13,
    fillClass: 'fill-primary',
    defaultColor: 'hsl(var(--primary))',
    palette: [
      'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))',
      'hsl(var(--chart-4))', 'hsl(var(--chart-5))',
    ],
    // Free, keyless OpenFreeMap style (real streets + labels). The MapLibre
    // demotiles style (country outlines only) read as unfinished — F1 swap.
    // Offline fallback: 'https://demotiles.maplibre.org/style.json'.
    // SEN-165: the offline e2e build (VITE_E2E_OFFLINE, see vite.offline.ts)
    // uses an INLINE blank style — no request at all, so an e2e run can neither
    // depend on the tile CDN nor race the map aborting its own style fetch
    // when a rebind rebuilds it. Markers and outlines render as usual.
    tileStyle: import.meta.env.VITE_E2E_OFFLINE === '1'
      ? E2E_OFFLINE_MAP_STYLE
      : import.meta.env.VITE_MAP_STYLE_URL ?? 'https://tiles.openfreemap.org/styles/liberty',
    /** The OUTER frame: fixed height in a document (full/peek); at block
     *  density the tile is the height (h-full), like every chart. */
    containerClass: 'relative h-[420px] w-full overflow-hidden rounded border border-border',
    containerBlockClass: 'relative min-h-0 w-full flex-1 overflow-hidden rounded border border-border',
    /** SEN-433: edge to edge in its card (DensityPolicy.edge_to_edge) — the
     *  card is the frame; its bottom corners round the map (Sentinel's
     *  `rounded-b-lg` on a `CardContent p-0` body). */
    containerEdgeClass: 'relative h-full min-h-0 w-full flex-1 overflow-hidden rounded-b-lg',
    /** The maplibre CONTAINER itself. maplibre-gl.css sets
     *  `.maplibregl-map { position: relative; overflow: hidden }` on it, so
     *  `absolute inset-0` is overridden and the element collapses to 0px —
     *  clipping the basemap AND every marker (SEN-1721: 'legend over an
     *  empty canvas'). The container must own its height: h-full. */
    canvasClass: 'h-full w-full',
    markerBorderColor: 'hsl(var(--background))',
    markerBorderWidth: '1px',
    markerOpacity: 0.8,
    outlineFillOpacity: 0.42,
    outlineLineWidth: 1.5,
    /** SEN-1733 icon stack: overlapping circles per referenced record */
    stack: { circlePx: 18, overlap: 0.62, max: 4, glyphClass: 'h-3 w-3', glyphColor: 'hsl(var(--background))',
             initialClass: 'text-[10px] font-semibold leading-none',
             badgeClass: 'rounded-full bg-muted px-1 text-[10px] leading-4 text-muted-foreground' },
    fitPadding: 48,
    maxZoom: 12,
    fitDuration: 0,
    legend: {
      className: 'absolute bottom-2 left-2 z-10 flex flex-col items-start gap-1 rounded border border-border bg-background/90 p-2',
      /** the categorical color legend docks right; swatchSize in px */
      colorPositionClass: 'left-auto right-2',
      swatchSize: 10,
      circleClass: 'inline-block border border-muted-foreground/60 bg-primary/20',
      labelClass: 'text-xs text-muted-foreground',
      titleClass: 'mb-1 text-xs font-medium text-muted-foreground',
    },
  },
  pivot: {
    cellPadding: 6,
    cellClass: 'border border-border px-2 py-1 text-right text-xs',
    headerClass: 'border border-border px-2 py-1 text-left text-xs font-medium text-muted-foreground',
    /** GAP D: the row whose record is the master filter's pivot record. */
    rowPivotClass: 'bg-[hsl(var(--primary)/0.08)] font-medium',
  },
  /** offsetPx: pixels to lift a data value label above its mark (bar top / line point / scatter point).
   *  The label gate is `budgets.valueLabelPx` via `ViewScale.showValueLabel`. */
  valueLabel: { ...CHART_LOOK.valueLabel, offsetPx: 4 },
  unitCaption: CHART_LOOK.unitCaption,
  /** SEN-1419: dashboard infinite canvas. Widgets are absolutely positioned by
   *  grid {x,y,w,h} in CELL units; the canvas scrolls both axes. Background is
   *  CROSSED graph-paper (Dashboards); dotted is reserved for Workflows —
   *  convention documented in poiesis/DESIGN.md. */
  dashboard: {
    /** side of one grid cell, px — the ONE unit every position/size derives
     *  from, at full width. The page FITS the cell to its container so that
     *  `pageCells` columns are always visible (a 12-wide composition on a
     *  1440 laptop overflowed by 60px — design review 2026-09-02): the cell
     *  shrinks down to `cellMinPx`, never below, and the canvas stays
     *  infinite beyond the page width. Density follows the tile size. */
    cellPx: 104,
    cellMinPx: 72,
    /** the "page width" in cells every composition should fit — the agent
     *  and the placement shim both compose against 12 */
    pageCells: 12,
    /** gutter between tiles, px — tiles are CARDS on the warm ground, not
     *  cells of a spreadsheet (design review 2026-09-02, F5) */
    tileGapPx: 12,
    /** inset of the whole grid from the canvas edge, px — the first column
     *  and row must not sit flush against the work card (CEO 2026-09-02:
     *  "no spacing between the left hand menu and the first row of tiles") */
    insetPx: 12,
    /** the tile: a Lvl-1 card (DESIGN.md layer model) — warm surface, work
     *  shadow, tight radius (stuck); the title is a muted caption INSIDE the
     *  padding, no divider strip */
    tileClass: 'group absolute flex min-w-0 flex-col overflow-hidden rounded-lg border border-border/60 bg-card shadow-work transition-shadow duration-150 hover:shadow-lift focus-within:shadow-lift',
    /** SEN-433 (§4.1): the `rows` page layout. Rows sit 24px apart (Sentinel's
     *  page rhythm, `space-y-6`); a row of self-framed widgets uses the summary
     *  grid rule + gap-4; any other row is Sentinel's weighted grid from lg —
     *  Σw equal tracks (`--w`), each widget spanning its own w (`--s`), gap-4 —
     *  and one column below. */
    rowsClass: CARD_LOOK.rowsClass,
    rowsFramedRowClass: CARD_LOOK.rowsFramedRowClass,
    rowsWeightedRowClass: 'grid min-w-0 gap-4 lg:[grid-template-columns:var(--w)]',
    /** a weighted cell stretches its card to the row's height (Sentinel's Card IS the grid item) */
    rowsWeightedCellClass: 'flex min-w-0 flex-col [&>*]:flex-1 lg:[grid-column:span_var(--s)/span_var(--s)]',
    /** `grid.h` units → an edge-to-edge card body's height (px): the map's h 7 is 350px. A padded body
     *  (a list) sizes to its content and its card stretches to the row (Sentinel's cards, class for class). */
    rowsBodyUnitPx: 50,
    /** Card chrome around a non-self-framed widget in `rows` — Sentinel's
     *  Card / CardHeader / CardTitle / CardDescription / Badge outline, class
     *  for class (§4.2). */
    cardClass: CARD_LOOK.cardClass,
    cardHeaderClass: CARD_LOOK.cardHeaderClass,
    cardHeaderRowClass: CARD_LOOK.cardHeaderRowClass,
    /** Sentinel's CardTitle is `cn('font-semibold leading-none tracking-tight', 'text-lg …')`, and
     *  tailwind-merge drops `leading-none` for text-lg's own line height (28px): the class as it renders */
    cardTitleClass: CARD_LOOK.cardTitleClass,
    cardIconClass: 'h-5 w-5',
    cardSubtitleClass: CARD_LOOK.cardSubtitleClass,
    cardBadgeClass: CARD_LOOK.cardBadgeClass,
    cardBodyClass: CARD_LOOK.cardBodyClass,
    cardBodyEdgeClass: CARD_LOOK.cardBodyEdgeClass,
    /** the hover ⚙ ✕ over a card or a self-framed widget in `rows` */
    rowsControlsClass: 'absolute right-2 top-2 z-10 flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100',
    /** SEN-433: a self-framed kind's tile (the stat's KpiCard IS the card):
     *  no border, no surface, no header — only the hover controls, as an
     *  overlay strip on the card's top edge (tileOverlayHeaderClass). */
    selfFramedTileClass: 'group absolute flex min-w-0 flex-col',
    tileOverlayHeaderClass: 'absolute inset-x-0 top-0 z-10 flex h-7 cursor-grab touch-none select-none items-center justify-end gap-2 px-2',
    selfFramedBodyClass: 'min-h-0 flex-1',
    tileHeaderClass: 'flex shrink-0 cursor-grab touch-none select-none items-center justify-between gap-2 px-3 pt-2.5 pb-1',
    tileTitleClass: 'truncate text-xs font-medium text-muted-foreground',
    /** the tile's own controls (filter mark, edit, remove) appear on hover /
     *  keyboard focus — a reading surface shows data, not chrome (Attio) */
    tileControlsClass: 'flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100',
    tileBodyClass: 'min-h-0 flex-1 overflow-hidden px-2 pb-2',
    /** the ground the cards sit on; graph paper appears only while a tile is
     *  being moved or resized (the editing metaphor), never as décor */
    canvasClass: 'relative bg-muted/40',
    /** scrollable margin beyond the widget bounding box, in cells */
    marginCells: 6,
    /** crossed graph-paper lines — border token, dark-aware */
    gridLineColor: 'hsl(var(--border))',
    gridLineWidth: 1,
    /** resize floor, cells */
    minW: 3,
    minH: 2,
    /** SEN monthly-store-report Task 10 (layout benchmark = the April report's
     *  document language: small eyebrow → H2 → one-line lede, generous rhythm
     *  BETWEEN sections — not a full-height tile per section). A `text` tile
     *  carries no chart and no interactive grid; a heading + one-line lede
     *  fits in a single cell row. Kept separate from `minH` (the resize floor
     *  for view tiles, which need room for axes/legends/rows) rather than
     *  lowering `minH` itself — that floor stays load-bearing for every
     *  chart/table kind. Read via `minHeightFor(view_kind)`, never inlined. */
    textMinH: 1,
    /** wrap width (cells) for the one-time reading-order auto-placement shim */
    autoFlowCols: 12,
    /** text-widget character cap — keeps config.text inside the 8KB config cap
     *  with JSON-escaping headroom */
    textMaxChars: 6000,  // H3: escape headroom under the 8KB byte cap (newlines/quotes serialize 2×)
  },
  /** SEN-1768 — the strip's chip grammar. The strip is a row of chips whose
   *  STATE is the information: nothing applied vs something applied vs the
   *  page's scope. Every chip reads these tokens; none holds its own colour,
   *  border or spacing. Reference: Attio's filter/sort bar (CEO screenshot,
   *  2026-09-04). */
  strip: {
    // fix-round-2 R9: tightened from `gap-1.5 px-2.5` — the strip's own
    // per-chip chrome (padding × ~7 chips + internal icon/label/value gaps)
    // was itself a real fraction of the ~90px the row measured over budget
    // at 1440px, on top of the group card (removed, R10) and the y binder's
    // uncapped width (capped, R9/N3). `gap-1` / `px-2` keep the chip legible
    // while giving fixed-width labels (Filter/Configure/Period) room to
    // never need to shrink for typical content.
    base: 'inline-flex min-w-0 items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors',
    /** nothing bound / nothing applied — dashed outline says "add one" */
    empty: 'border border-dashed border-border bg-transparent text-muted-foreground hover:border-primary/50 hover:bg-muted hover:text-foreground',
    /** fix-round-2 R11: moved OFF brand blue. Fix-round-1's `bg-primary/10`
     *  fixed the active/scope contrast but collided with the focus ring
     *  (also `hsl(var(--ring))` = `--primary`) — a focused chip and an
     *  applied chip read as the same signal (CEO + task-5-fix1-rereview.md).
     *  `bg-secondary` is the calibrated neutral: it's the EXACT fill the
     *  sidebar uses for its own selected-row highlight (measured
     *  (244,241,235) directly off the sidebar in the round-1 screenshot),
     *  so "applied" now shares its visual language with an already-familiar
     *  selected/active affordance elsewhere in the shell, not with focus.
     *  Hover deepens to `bg-border` (232,227,216 — `--border: 40 26% 88%`,
     *  strictly darker than `--secondary: 40 28% 94%`), never lighter. */
    active: 'border border-border bg-secondary text-foreground hover:bg-border',
    /** fix-round-2 R11: `scope` (Period) needed "a quiet but real boundary" —
     *  fix-round-1's `border-transparent` over-corrected into no affordance
     *  at all (task-5-fix1-rereview.md N5: Period read as inert text between
     *  two bordered ChipButtons on the dashboard). `border-border/60` is a
     *  real, always-visible outline, just quieter than `active`'s solid
     *  `border-border` + filled `bg-secondary` — the two remain
     *  distinguishable by FILL (active has one, scope doesn't), not by
     *  competing for the same hue. */
    scope: 'border border-border/60 bg-background text-foreground hover:border-border hover:bg-muted',
    /** fix-round-2 R9: a chip that must shrink ellipsises its label instead
     *  of overflowing it. `shrink-0` (round 0/1) meant `strip.base`'s
     *  `min-w-0` let the BUTTON shrink while the label refused to, so the
     *  text overflowed the shrunken box with no ellipsis and the next
     *  opaque sibling painted over it (task-5-fix1-rereview.md N1 —
     *  "Configu" clipped mid-glyph at 1440px). `min-w-0 truncate` lets the
     *  label itself shrink and ellipsise before that happens. */
    labelClass: 'min-w-0 truncate text-muted-foreground',
    /** fix-round-1 R4: value emphasis is STATE-AWARE — `empty`'s placeholder
     *  ("select…") must read as muted invitation text, not a bound value; the
     *  single `font-medium text-foreground` applied to every state (the
     *  original cut) made an unbound "series select…" read as bold as a
     *  genuinely bound "group Building Type" — a real regression against the
     *  pre-task look (`**series** | select…`, muted placeholder). */
    valueClass: {
      empty: 'min-w-0 truncate text-muted-foreground',
      active: 'min-w-0 truncate font-medium text-foreground',
      scope: 'min-w-0 truncate font-medium text-foreground',
    },
    iconClass: 'h-3.5 w-3.5 shrink-0 text-muted-foreground',
    /** the required marker of an unbound REQUIRED role — inside its own chip */
    requiredClass: 'shrink-0 leading-none text-destructive',
    /** "+N" for the applied keys an active chip does not name */
    countClass: 'shrink-0 rounded-full bg-muted px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground',
    /** fix-round-2 R10 (CEO, with Attio in hand): the card is GONE.
     *  Fix-round-1's `border … bg-muted/30 p-1` (R7) was itself the "grey
     *  slab" the CEO called out — the heaviest, most bordered element on the
     *  strip, wrapped around its LEAST important chips. Attio doesn't card
     *  its control groups; a divider is the seam. `StripDivider` on both
     *  sides of the binder block (App.tsx) now carries the entire "these are
     *  one group" signal — no border, no tint, no padding here. */
    groupClass: 'inline-flex min-w-0 items-center gap-1.5',
    /** …which wraps at narrow viewports (SEN-702: nothing pushed off-screen
     *  at ~900px). SEN-1773: from lg (1024) up the group must NOT wrap — with
     *  flex-wrap, a few px of deficit dropped the last chip under the row
     *  (the two-line strip at 1440) instead of letting chips truncate; nowrap
     *  re-engages flex-shrink and the chips' own min-w-0/truncate grammar. */
    groupWrapClass: 'inline-flex min-w-0 flex-wrap items-center gap-1.5 lg:flex-nowrap',
    /** …divided from Filter/Sort, and (fix-round-2 R10) from Configure/Period
     *  on the other side — round 1 only added the FIRST divider; Configure
     *  and Period trailed the group with no seam at all
     *  (task-5-fix1-rereview.md: "the divider before page scope half of the
     *  reference is not delivered"). */
    dividerClass: 'mx-1 h-4 w-px shrink-0 bg-border',
    /** fix-round-2 R12: the reduce-role (`y`) binder's native `<select>` is
     *  visually part of its own `StripChip`-token span (ViewBindingBar.tsx)
     *  but must render with NO control chrome of its own — this is that
     *  "invisible" select styling as a token, not literal classes at the
     *  call site (task-5-fix1-rereview.md N4). */
    selectClass: 'border-0 bg-transparent p-0 text-xs outline-none',
    /** SEN-1773: the binder select is an INVISIBLE OVERLAY over a text value
     *  — a native <select> sizes to its longest OPTION, not its value (one
     *  50-char quantity name measured the y binder at 336px and wrapped the
     *  binder group at 1440). The chip is sized by the truncating text span;
     *  this stretches the real control over it, keeping the native dropdown
     *  and keyboard access. */
    selectOverlayClass: 'absolute inset-0 cursor-pointer opacity-0',
    /** a role-binder chip's width cap — a bound 2-hop path ("Crate › Colour")
     *  must truncate rather than push the rest of the strip off-screen */
    /** SEN-1773: 14rem → 10rem. The measured 1440 budget: fixed controls +
     *  the applied-condition chip leave ~506px for the binder group; at 14rem
     *  one long bound quantity name (224px) forced the group to wrap its last
     *  chip under the row. 10rem keeps every chip identifiable (a truncating
     *  value carries a full-name title) and the group on ONE row at 1440. */
    binderClass: 'max-w-[10rem]',
    /** SEN-1773: the applied-condition chip's ATTRIBUTE segment floor — an
     *  attribute name is the least truncatable thing on the strip (measured
     *  103px → `· E…`, a single letter, at 1440). The floor guarantees an
     *  identifiable prefix; the VALUE segment concedes width first. */
    chipAttrMinClass: 'min-w-[5.5rem]',
    /** fix-round-2 R9: Filter/Sort/Configure are fixed, short, essential
     *  controls — at 1440px they must never be the ones asked to shrink.
     *  Without this, the browser's default `flex-shrink:1` on every chip
     *  distributes ANY row overflow across ALL of them, so even a short,
     *  constant label like "Configure" loses characters to a LONG, variable
     *  binder VALUE it has nothing to do with (task-5-fix1-rereview.md N1).
     *  `binderClass`'s `max-w` caps a binder's ceiling; THIS forces the
     *  shrink burden itself onto the binder group alone, which R9 already
     *  sanctions conceding space ("a chip that must shrink ellipsises its
     *  label") — Period needs no equivalent token: its `ml-auto` wrapper
     *  already keeps it out of the row's flex-shrink distribution. */
    triggerClass: 'shrink-0',
    /** ONE editor row: handle · breadcrumb path · control · remove */
    row: {
      className: 'flex items-center gap-2 rounded-sm px-1 py-1 text-xs hover:bg-muted/60',
      handleClass: 'shrink-0 cursor-grab select-none text-muted-foreground',
      pathClass: 'min-w-0 flex-1 truncate font-medium text-foreground',
      controlClass: 'shrink-0 rounded-sm border border-border bg-background px-1.5 py-0.5 text-xs tabular-nums hover:bg-muted',
      removeClass: 'shrink-0 rounded-sm px-1 leading-none text-muted-foreground hover:bg-muted hover:text-foreground',
      addClass: 'mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline',
    },
  },
} as const

export type ViewTheme = typeof viewTheme
